/**
 * GraphQL Invite Hooks
 * Feature: 647-invite-registration
 *
 * Hooks for invite token operations:
 * - Create invite token (admin only)
 * - List invite tokens (admin only)
 * - Get single invite token (admin only)
 * - Validate invite token (public)
 * - Register with invite (public)
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useLazyQuery, gql } from '@apollo/client';
import logger from '../../utils/logger';

// =============================================================================
// GraphQL Definitions
// =============================================================================

const CREATE_INVITE_TOKEN_MUTATION = gql`
  mutation CreateInviteToken($input: CreateInviteTokenInput) {
    createInviteToken(input: $input) {
      inviteToken {
        id
        globalId
        maxUses
        useCount
        usesRemaining
        isActive
        allowsAdminEmail
        createdAt
        createdBy {
          id
          globalId
          username
          displayName
        }
      }
      inviteUrl
      nonce
    }
  }
`;

const INVITE_TOKENS_QUERY = gql`
  query InviteTokens {
    inviteTokens {
      id
      globalId
      maxUses
      useCount
      usesRemaining
      isActive
      allowsAdminEmail
      createdAt
      createdBy {
        id
        globalId
        username
        displayName
      }
    }
  }
`;

const INVITE_TOKEN_QUERY = gql`
  query InviteToken($id: ID!) {
    inviteToken(id: $id) {
      id
      globalId
      maxUses
      useCount
      usesRemaining
      isActive
      allowsAdminEmail
      createdAt
      createdBy {
        id
        globalId
        username
        displayName
      }
      registeredUsers {
        id
        globalId
        username
        displayName
        email
        createdAt
      }
    }
  }
`;

const VALIDATE_INVITE_TOKEN_QUERY = gql`
  query ValidateInviteToken($input: ValidateInviteTokenInput!) {
    validateInviteToken(input: $input) {
      isValid
      message
      usesRemaining
    }
  }
`;

const REGISTER_WITH_INVITE_MUTATION = gql`
  mutation RegisterWithInvite($input: RegisterWithInviteInput!) {
    registerWithInvite(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        createdAt
        updatedAt
      }
      token
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface User {
  id: string;
  globalId: string;
  username: string;
  displayName: string;
  email?: string;
  createdAt?: string;
}

interface InviteToken {
  id: string;
  globalId: string;
  maxUses: number;
  useCount: number;
  usesRemaining: number;
  isActive: boolean;
  allowsAdminEmail: boolean;
  createdAt: string;
  createdBy: User;
  registeredUsers?: User[];
}

interface CreateInviteTokenPayload {
  inviteToken: InviteToken;
  inviteUrl: string;
  nonce: string;
}

interface InviteTokenValidation {
  isValid: boolean;
  message: string;
  usesRemaining: number | null;
}

interface RegisterWithInviteInput {
  email: string;
  username: string;
  displayName: string;
  password: string;
  inviteToken: string;
}

interface AuthPayload {
  user: User;
  token: string;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook for creating a new invite token (admin only)
 *
 * Creates an invite token with an optional maxUses limit and allowsAdminEmail flag.
 * Returns the invite URL (which includes the nonce) for sharing.
 */
export function useCreateInviteTokenGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createInviteToken: CreateInviteTokenPayload },
    { input?: { maxUses?: number; allowsAdminEmail?: boolean } }
  >(CREATE_INVITE_TOKEN_MUTATION);

  const createInviteToken = async (maxUses?: number, allowsAdminEmail?: boolean) => {
    logger.info('Admin', 'Creating invite token via GraphQL', { maxUses, allowsAdminEmail });

    const input: { maxUses?: number; allowsAdminEmail?: boolean } = {};
    if (maxUses !== undefined) input.maxUses = maxUses;
    if (allowsAdminEmail !== undefined) input.allowsAdminEmail = allowsAdminEmail;

    const result = await mutate({
      variables: { input: Object.keys(input).length > 0 ? input : undefined },
      refetchQueries: ['InviteTokens'],
    });

    if (result.data?.createInviteToken) {
      logger.info('Admin', 'Invite token created', {
        tokenId: result.data.createInviteToken.inviteToken.id,
        maxUses: result.data.createInviteToken.inviteToken.maxUses,
        allowsAdminEmail: result.data.createInviteToken.inviteToken.allowsAdminEmail,
      });
    }

    return result.data?.createInviteToken;
  };

  return {
    createInviteToken,
    data: data?.createInviteToken,
    loading,
    error,
  };
}

/**
 * Hook for listing all invite tokens (admin only)
 *
 * Fetches all invite tokens with usage statistics.
 * Uses network-only fetch policy to always get fresh data.
 */
export function useInviteTokensGraphQL() {
  const { data, loading, error, refetch } = useQuery<
    { inviteTokens: InviteToken[] }
  >(INVITE_TOKENS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  return {
    inviteTokens: data?.inviteTokens ?? [],
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for getting a single invite token with details (admin only)
 *
 * Fetches a specific invite token including registered users.
 */
export function useInviteTokenGraphQL(id: string | null) {
  const { data, loading, error, refetch } = useQuery<
    { inviteToken: InviteToken | null },
    { id: string }
  >(INVITE_TOKEN_QUERY, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: id || 'skip-placeholder' },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  return {
    inviteToken: data?.inviteToken ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for validating an invite token (public)
 *
 * Validates the invite token nonce before showing the registration form.
 * Returns whether the token is valid and how many uses remain.
 */
export function useValidateInviteTokenGraphQL() {
  const [validate, { data, loading, error }] = useLazyQuery<
    { validateInviteToken: InviteTokenValidation },
    { input: { nonce: string } }
  >(VALIDATE_INVITE_TOKEN_QUERY, {
    fetchPolicy: 'network-only',
  });

  const validateInviteToken = useCallback(async (nonce: string) => {
    logger.info('Auth', 'Validating invite token');

    const result = await validate({ variables: { input: { nonce } } });

    if (result.data?.validateInviteToken) {
      logger.info('Auth', 'Invite token validation result', {
        isValid: result.data.validateInviteToken.isValid,
      });
    }

    return result.data?.validateInviteToken;
  }, [validate]);

  return {
    validateInviteToken,
    validation: data?.validateInviteToken ?? null,
    loading,
    error,
  };
}

/**
 * Hook for registering a new user with an invite token (public)
 *
 * Creates a new user account using a valid invite token.
 * On success, stores the token and resets the Apollo cache.
 */
export function useRegisterWithInviteGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { registerWithInvite: AuthPayload },
    { input: RegisterWithInviteInput }
  >(REGISTER_WITH_INVITE_MUTATION);

  const registerWithInvite = async (input: RegisterWithInviteInput) => {
    logger.info('Auth', 'Registering with invite token via GraphQL');

    const result = await mutate({ variables: { input } });

    if (result.data?.registerWithInvite) {
      const { token, user } = result.data.registerWithInvite;

      // Store token and user in localStorage (matching existing auth pattern)
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      logger.info('Auth', 'Registration with invite successful', { userId: user.id });
    }

    return result.data?.registerWithInvite;
  };

  return {
    registerWithInvite,
    data: data?.registerWithInvite,
    loading,
    error,
  };
}

// Export types for consumers
export type { InviteToken, CreateInviteTokenPayload, InviteTokenValidation, User as InviteUser, RegisterWithInviteInput };
